// iter v29 r213 — Pull-to-refresh hook · iOS-native feel.
//
// Pattern: l'utente trascina il dito verso il basso quando è a scrollTop=0.
// Dopo 70px di pull, attiva l'haptic e mostra l'indicatore. Al rilascio sopra
// la soglia chiama `onRefresh()`.
//
// Caratteristiche:
//   - Funziona solo se scrollY === 0 (rispetta lo scroll naturale)
//   - Damping non-lineare (60% del pull, sembra "elastico")
//   - Su iOS native chiama Haptics.impact medium al trigger
//   - Auto-cleanup listener
//
// Uso:
//   const { isPulling, isRefreshing, pullDistance, indicatorRef } = usePullToRefresh(async () => {
//     await reloadData();
//   });
//   <PullIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />

import { useEffect, useRef, useState, useCallback } from "react";

const TRIGGER_THRESHOLD = 70;
const MAX_PULL = 120;
const DAMPING = 0.55;

export function usePullToRefresh(onRefresh) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(null);
  const triggeredHaptic = useRef(false);

  const triggerHaptic = useCallback(() => {
    if (triggeredHaptic.current) return;
    triggeredHaptic.current = true;
    try {
      if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
        import("@capacitor/haptics").then(({ Haptics, ImpactStyle }) => {
          Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
        }).catch(() => {});
      }
    } catch {/* no-op */}
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleTouchStart = (e) => {
      if (window.scrollY > 0 || isRefreshing) return;
      startY.current = e.touches[0].clientY;
      triggeredHaptic.current = false;
    };

    const handleTouchMove = (e) => {
      if (startY.current === null || isRefreshing) return;
      const currentY = e.touches[0].clientY;
      const delta = currentY - startY.current;
      if (delta <= 0) {
        setPullDistance(0);
        return;
      }
      // Damped pull (elastic feel)
      const dampedDelta = Math.min(MAX_PULL, delta * DAMPING);
      setPullDistance(dampedDelta);
      if (dampedDelta >= TRIGGER_THRESHOLD) triggerHaptic();
    };

    const handleTouchEnd = async () => {
      if (startY.current === null) return;
      const shouldRefresh = pullDistance >= TRIGGER_THRESHOLD;
      startY.current = null;
      if (shouldRefresh && !isRefreshing && typeof onRefresh === "function") {
        setIsRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [onRefresh, pullDistance, isRefreshing, triggerHaptic]);

  return {
    pullDistance,
    isRefreshing,
    isPulling: pullDistance > 0,
    isReady: pullDistance >= TRIGGER_THRESHOLD,
  };
}

export default usePullToRefresh;
